import React, { useEffect, useState } from "react";

interface Distributor {
  distributor_id: string;
  business_name: string;
  email: string;
  pincode: string;
  stock_value: number;
  orders_count: number;
  service_active: boolean;
  shop_status: boolean;
}

const FinDashboard: React.FC = () => {
  const [distributors, setDistributors] = useState<Distributor[]>([]);
  const [search, setSearch] = useState("");
  const [lastUpdated, setLastUpdated] = useState("");

  useEffect(() => {
    updateLastUpdated();
    const interval = setInterval(updateLastUpdated, 60000);
    return () => clearInterval(interval);
  }, []);

  const updateLastUpdated = () => {
    const now = new Date();
    const time = now.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
    setLastUpdated(time);
  };

  const filtered = distributors.filter(
    (d) =>
      d.distributor_id.toLowerCase().includes(search.toLowerCase()) ||
      d.business_name.toLowerCase().includes(search.toLowerCase()) ||
      d.pincode.toLowerCase().includes(search.toLowerCase())
  );

  const total = distributors.length;
  const active = distributors.filter((d) => d.service_active && d.shop_status).length;
  const totalOrders = distributors.reduce((sum, d) => sum + d.orders_count, 0);
  const totalStock = distributors.reduce((sum, d) => sum + d.stock_value, 0);
  const activePercentage = total ? Math.round((active / total) * 100) : 0;

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-500 to-blue-500 p-8">
      {/* Header */}
      <div className="bg-white shadow-lg border-b-4 border-purple-500 rounded-lg p-6 mb-8">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">
              Marketing Executive Dashboard
            </h1>
            <p className="text-sm text-gray-600">
              Distributor Overview & Performance
            </p>
          </div>
          <div className="text-right">
            <p className="text-xs text-gray-500">Last Updated</p>
            <p className="font-bold">{lastUpdated}</p>
          </div>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <KpiCard title="Total Distributors" value={total} color="purple" icon="👥" />
        <KpiCard title="Active Distributors" value={active} color="green" icon="✓" extra={`${activePercentage}%`} />
        <KpiCard title="Total Orders" value={totalOrders} color="blue" icon="📦" />
        <KpiCard title="Total Stock" value={`₹${totalStock.toLocaleString()}`} color="orange" icon="📊" />
      </div>

      {/* Distributor Table */}
      <div className="bg-white rounded-xl shadow-lg p-6">
        <div className="flex justify-between mb-4">
          <h2 className="text-xl font-bold">📋 Distributor List</h2>
          <input
            type="text"
            placeholder="Search by name, ID, or pincode..."
            className="px-4 py-2 border rounded-lg w-80"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        <table className="w-full text-sm">
          <thead className="bg-gray-100 text-left">
            <tr>
              <th className="p-3">Distributor ID</th>
              <th className="p-3">Name</th>
              <th className="p-3">Pincode</th>
              <th className="p-3">Stock</th>
              <th className="p-3">Orders</th>
              <th className="p-3">Status</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((d) => (
              <tr key={d.distributor_id} className="border-b hover:bg-gray-50">
                <td className="p-3 font-mono">{d.distributor_id}</td>
                <td className="p-3">
                  <div>
                    <p className="font-bold">{d.business_name}</p>
                    <p className="text-xs text-gray-500">{d.email}</p>
                  </div>
                </td>
                <td className="p-3">{d.pincode}</td>
                <td className="p-3">₹{d.stock_value.toLocaleString()}</td>
                <td className="p-3">{d.orders_count}</td>
                <td className="p-3">
                  {d.service_active && d.shop_status ? "Active" : "Inactive"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {filtered.length === 0 && (
          <div className="text-center py-10 text-gray-500">
            No Distributors Found
          </div>
        )}
      </div>
    </div>
  );
};

const KpiCard = ({
  title,
  value,
  color,
  icon,
  extra,
}: any) => (
  <div className={`bg-white rounded-xl shadow-lg p-6 border-l-4 border-${color}-500`}>
    <div className="flex justify-between items-center mb-3">
      <p className="text-sm font-semibold text-gray-600 uppercase">{title}</p>
      <div className="text-2xl">{icon}</div>
    </div>
    <p className={`text-3xl font-bold text-${color}-600`}>{value}</p>
    {extra && <p className="text-xs mt-1">{extra} of total</p>}
  </div>
);

export default FinDashboard;